// Lightweight bridge between the raw fetch client (api.ts) and the outlet
// feature flags (outletFeatures.ts), like subscription-bus.ts for billing.
//
// Dhaba support can switch a feature (today: CRM) off for one outlet. Every
// API of that feature then answers 403 {code: "FEATURE_DISABLED",
// details: {feature, outlet_id}}. api.ts reports such a refusal here; the
// flag module listens and flips the stored flag so the UI hides the feature
// the moment the server refuses. This module imports nothing, so api.ts can
// use it without an import cycle.

export const FEATURE_DISABLED_CODE = "FEATURE_DISABLED";

export interface FeatureDisabledSignal {
  /** The switched-off feature, e.g. "crm". */
  feature: string;
  /** The outlet it is off for, when the server said so. */
  outletId?: string;
}

/** The feature a 403 body says is switched off, or null for any other response. */
export function featureDisabledSignal(status: number, body: unknown): FeatureDisabledSignal | null {
  if (status !== 403 || !body || typeof body !== "object") return null;
  const { code, details } = body as { code?: unknown; details?: unknown };
  if (code !== FEATURE_DISABLED_CODE || !details || typeof details !== "object") return null;
  const { feature, outlet_id } = details as { feature?: unknown; outlet_id?: unknown };
  if (typeof feature !== "string" || !feature) return null;
  return { feature, outletId: typeof outlet_id === "string" && outlet_id ? outlet_id : undefined };
}

type FeatureDisabledHandler = (signal: FeatureDisabledSignal) => void;

const handlers = new Set<FeatureDisabledHandler>();

/** Listen for feature-disabled refusals. Returns an unsubscribe function. */
export function onFeatureDisabled(handler: FeatureDisabledHandler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

/** Called by the API layer for every non-OK response; acts only on 403 FEATURE_DISABLED. */
export function reportFeatureDisabled(status: number, body: unknown): void {
  const signal = featureDisabledSignal(status, body);
  if (!signal) return;
  handlers.forEach((handler) => handler(signal));
}
